import type { QueryDocumentSnapshot } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/Button';
import EmptyState from '../../../components/EmptyState';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { questsService } from '../services/quests-service';
import type { CompiledQuest, QuestType } from '../services/questTypes';
import { QuestResultCard } from './quest-results/QuestResultCard';
import { QuestResultFilters } from './quest-results/QuestResultFilters';

export const QuestResults = () => {
    const [quests, setQuests] = useState<CompiledQuest[]>([]);
    const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [selectedQuestType, setSelectedQuestType] = useState<QuestType | ''>('');
    const [selectedJourneyDay, setSelectedJourneyDay] = useState<number | ''>('');
    const [selectedUserId, setSelectedUserId] = useState<string>('');

    const loadQuests = async (isLoadMore = false, lastDocToUse?: QueryDocumentSnapshot | null) => {
        setLoading(true);
        try {
            const result = await questsService.getAllCompiledQuests(50, isLoadMore ? lastDocToUse ?? undefined : undefined);
            setQuests(prev => isLoadMore ? [...prev, ...result.quests] : result.quests);
            setLastDoc(result.lastDoc);
        } catch (error) {
            console.error('Error loading quests:', error);
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    useEffect(() => {
        loadQuests();
    }, []);

    const loadedCount = useMemo(() => quests.length, [quests]);

    const questTypes = useMemo(() => Array.from(new Set(quests.map((quest) => quest.type))), [quests]);
    const journeyDays = useMemo(() => Array.from(new Set(quests.map((quest) => quest.journeyDay))), [quests]);

    const filteredQuests = useMemo(() => {
        return quests.filter((quest) => {
            if (selectedQuestType !== '' && quest.type !== selectedQuestType) return false;
            if (selectedJourneyDay !== '' && quest.journeyDay !== selectedJourneyDay) return false;
            if (selectedUserId !== '' && quest.userId !== selectedUserId) return false;
            return true;
        });
    }, [quests, selectedQuestType, selectedJourneyDay, selectedUserId]);

    const filteredCount = useMemo(() => filteredQuests.length, [filteredQuests]);

    if (initialLoading) {
        return (
            <div className="p-4 flex justify-center items-center min-h-[400px]">
                <LoadingSpinner />
            </div>
        );
    }

    if (!initialLoading && quests.length === 0) {
        return (
            <div className="p-4">
                <EmptyState
                    title="No quests found"
                    description="There are no compiled quests in the database yet."
                />
            </div>
        );
    }

    return (
        <div className="p-4 space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-700">Quest Results ({filteredCount} of {loadedCount})</h2>
            </div>
            <QuestResultFilters
                questTypes={questTypes}
                journeyDays={journeyDays}
                selectedQuestType={selectedQuestType}
                selectedJourneyDay={selectedJourneyDay}
                selectedUserId={selectedUserId}
                onQuestTypeChange={setSelectedQuestType}
                onJourneyDayChange={setSelectedJourneyDay}
                onUserIdChange={setSelectedUserId} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredQuests.map((quest) => (
                    <QuestResultCard key={`${quest.id}-${quest.userId}`} quest={quest} />
                ))}
            </div>

            {loading && !initialLoading && (
                <div className="flex justify-center py-4">
                    <LoadingSpinner />
                </div>
            )}

            {lastDoc && (
                <div className="flex justify-center pt-4">
                    <Button
                        onClick={() => loadQuests(true, lastDoc)}
                        disabled={loading}
                        variant="outline"
                    >
                        Load More
                    </Button>
                </div>
            )}
        </div>
    );
};

